import React, { useState, useEffect } from "react";
import {
  Box,
  Grid,
  TextField,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormLabel,
  Typography,
  Checkbox,
  Autocomplete,
} from "@mui/material";
import { getAllVendorRegistrationList } from "../../../api/ap_services";
import SectionCard from "./SectionCard";
import FormikTextField from "./FormikTextField";

const categoryOptions = [
  { value: "Sewing", label: "Sewing" },
  { value: "Cutting", label: "Cutting" },
  { value: "Packing", label: "Packing" },
  { value: "Dyeing", label: "Dyeing" },
  { value: "Knitting", label: "Knitting" },
  { value: "Compacting", label: "Compacting" },
  { value: "Other", label: "Other" },
];

const Step1FactoryEvaluation = ({
  formData,
  onFieldChange,
  onPanBlur,
  errors,
  touched,
  setFieldValue,
  disable,
}) => {
  const fieldError = (name) => Boolean(touched?.[name] && errors?.[name]);
  const fieldHelper = (name) => (touched?.[name] && errors?.[name]) || "";
  const fieldsEnabled = Boolean(disable?.isQCTeam || disable?.isAdmin);

  const categoryValue = Array.isArray(formData.category)
    ? formData.category
    : formData.category
      ? [formData.category]
      : [];
  const handleCategoryChange = (value) => {
    if (!setFieldValue) return;
    const next = categoryValue.includes(value)
      ? categoryValue.filter((v) => v !== value)
      : [...categoryValue, value];
    setFieldValue("category", next);
  };
  const isOtherSelected = categoryValue.includes("Other");

  const referredForValue = Array.isArray(formData.referredFor)
    ? formData.referredFor
    : formData.referredFor
      ? [formData.referredFor]
      : [];
  const handleReferredForChange = (value) => {
    if (!setFieldValue) return;
    const next = referredForValue.includes(value)
      ? referredForValue.filter((v) => v !== value)
      : [...referredForValue, value];
    setFieldValue("referredFor", next);
  };
  const isReferredForOtherSelected = referredForValue.includes("Other");

  const [vendorComboOptions, setVendorComboOptions] = useState([]);
  useEffect(() => {
    getAllVendorRegistrationList(0, 9999).then((res) => {
      const list = res?.response?.responseObject ?? [];
      const options = Array.isArray(list)
        ? list.map((item) => ({
            vendorTypeId: item.vendorTypeId ?? item.vendor_type_id ?? item.id,
            code: item.vendorCode ?? item.vendor_code ?? "",
            name: item.companyName ?? item.company_name ?? "",
          }))
        : [];
      setVendorComboOptions(options);
    });
  }, []);

  // When loading existing record with vendorCode but no vendorTypeId (e.g. old data), sync vendorTypeId from matched option
  const vendorCodeForMatch = formData.vendorCode ?? formData.vendor_code;
  const vendorTypeIdFromForm = formData.vendorTypeId ?? formData.vendor_type_id;
  useEffect(() => {
    if (
      !setFieldValue ||
      (vendorTypeIdFromForm != null && vendorTypeIdFromForm !== "")
    )
      return;
    if (vendorComboOptions.length === 0 || !vendorCodeForMatch) return;
    const match = vendorComboOptions.find((o) => o.code === vendorCodeForMatch);
    if (match && match.vendorTypeId != null) {
      setFieldValue("vendorTypeId", match.vendorTypeId);
    }
  }, [
    vendorComboOptions,
    vendorCodeForMatch,
    vendorTypeIdFromForm,
    setFieldValue,
  ]);

  const isExistingUserYes = formData.isExistingUser === "Y";

  return (
    <Box>
      <SectionCard title="">
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <FormControl component="fieldset">
              <FormLabel component="legend">Is Existing user?</FormLabel>
              <RadioGroup
                row
                name="isExistingUser"
                value={formData.isExistingUser || ""}
                onChange={(e) => {
                  onFieldChange({
                    target: { name: "isExistingUser", value: e.target.value },
                  });
                  if (e.target.value === "N" && setFieldValue) {
                    setFieldValue("vendorTypeId", null);
                    setFieldValue("vendorCode", "");
                    setFieldValue("vendorCodeOption", null);
                  }
                }}
              >
                <FormControlLabel
                  disabled={!fieldsEnabled}
                  value="Y"
                  control={<Radio />}
                  label="Yes"
                />
                <FormControlLabel
                  disabled={!fieldsEnabled}
                  value="N"
                  control={<Radio />}
                  label="No"
                />
              </RadioGroup>
            </FormControl>
          </Grid>
          {isExistingUserYes && (
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={vendorComboOptions}
                sx={{ width: 300 }}
                getOptionLabel={(option) => {
                  if (typeof option === "string") return option;
                  const code = option.code ?? "";
                  const name = option.name ?? "";
                  return name ? `${code} - ${name}` : code || "";
                }}
                value={
                  vendorComboOptions.find(
                    (o) =>
                      String(o.vendorTypeId) ===
                      String(
                        formData.vendorTypeId ?? formData.vendor_type_id ?? "",
                      ),
                  ) ??
                  vendorComboOptions.find(
                    (o) =>
                      o.code === (formData.vendorCode ?? formData.vendor_code),
                  ) ??
                  formData.vendorCodeOption ??
                  null
                }
                onChange={(event, value) => {
                  if (setFieldValue) {
                    setFieldValue("vendorCodeOption", value);
                    setFieldValue(
                      "vendorCode",
                      value ? (value.code ?? "") : "",
                    );
                    setFieldValue(
                      "vendorTypeId",
                      value ? (value.vendorTypeId ?? null) : null,
                    );
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Vendor Code"
                    variant="outlined"
                  />
                )}
                size="small"
                disabled={!fieldsEnabled}
              />
            </Grid>
          )}
        </Grid>
      </SectionCard>

      <SectionCard title="Factory Details">
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="P No."
              name="piNo"
              value={formData.piNo || ""}
              onChange={onFieldChange}
              size="small"
              error={fieldError("piNo")}
              helperText={fieldHelper("piNo")}
              disabled={!fieldsEnabled}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Date"
              name="factoryDate"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={formData.factoryDate || ""}
              onChange={onFieldChange}
              size="small"
              error={fieldError("factoryDate")}
              helperText={fieldHelper("factoryDate")}
              disabled={!fieldsEnabled}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              label="Factory Name"
              name="factoryName"
              value={formData.factoryName || ""}
              onChange={onFieldChange}
              size="small"
              error={fieldError("factoryName")}
              helperText={fieldHelper("factoryName")}
              disabled={!fieldsEnabled}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              label="Factory Address"
              name="factoryAddress"
              value={formData.factoryAddress || ""}
              onChange={onFieldChange}
              size="small"
              error={fieldError("factoryAddress")}
              helperText={fieldHelper("factoryAddress")}
              disabled={!fieldsEnabled}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              label="Factory Manager / In-charge Name"
              name="factoryInchargeName"
              value={formData.factoryInchargeName || ""}
              onChange={onFieldChange}
              size="small"
              error={fieldError("factoryInchargeName")}
              helperText={fieldHelper("factoryInchargeName")}
              disabled={!fieldsEnabled}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Factory Manager / In-charge Title"
              name="factoryInchargeTitle"
              value={formData.factoryInchargeTitle || ""}
              onChange={onFieldChange}
              size="small"
              error={fieldError("factoryInchargeTitle")}
              helperText={fieldHelper("factoryInchargeTitle")}
              disabled={!fieldsEnabled}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              label="Factory Telephone"
              name="factoryPhone"
              value={formData.factoryPhone || ""}
              onChange={onFieldChange}
              size="small"
              error={fieldError("factoryPhone")}
              helperText={fieldHelper("factoryPhone")}
              disabled={!fieldsEnabled}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              label="Factory E-mail ID"
              name="factoryEmail"
              type="email"
              value={formData.factoryEmail || ""}
              onChange={onFieldChange}
              size="small"
              error={fieldError("factoryEmail")}
              helperText={fieldHelper("factoryEmail")}
              disabled={!fieldsEnabled}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              label="Year of Establishment"
              name="factoryYearOfEstablishment"
              value={formData.factoryYearOfEstablishment || ""}
              onChange={onFieldChange}
              size="small"
              error={fieldError("factoryYearOfEstablishment")}
              helperText={fieldHelper("factoryYearOfEstablishment")}
              disabled={!fieldsEnabled}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              label="Facility Size"
              name="factorySize"
              value={formData.factorySize || ""}
              onChange={onFieldChange}
              size="small"
              error={fieldError("factorySize")}
              helperText={fieldHelper("factorySize")}
              disabled={!fieldsEnabled}
            />
          </Grid>

          <Grid item xs={12}>
            <FormControl
              component="fieldset"
              error={fieldError("category")}
              required
            >
              <FormLabel component="legend">
                Category (select all that apply)
              </FormLabel>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 0.5 }}>
                {categoryOptions.map((opt) => (
                  <FormControlLabel
                    key={opt.value}
                    control={
                      <Checkbox
                        checked={categoryValue.includes(opt.value)}
                        onChange={() => handleCategoryChange(opt.value)}
                        name={`category_${opt.value}`}
                        disabled={!fieldsEnabled}
                      />
                    }
                    label={opt.label}
                  />
                ))}
              </Box>
              {fieldHelper("category") && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ mt: 0.5, display: "block" }}
                >
                  {fieldHelper("category")}
                </Typography>
              )}
            </FormControl>
          </Grid>
          {isOtherSelected && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Other - Description"
                name="categoryOtherRemarks"
                value={formData.categoryOtherRemarks || ""}
                onChange={onFieldChange}
                size="small"
                placeholder="Please describe"
                error={fieldError("categoryOtherRemarks")}
                helperText={fieldHelper("categoryOtherRemarks")}
                disabled={!fieldsEnabled}
              />
            </Grid>
          )}
        </Grid>
      </SectionCard>

      <SectionCard title="Owner / MD / Partner / Occupier details">
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              label="Name"
              name="ownerName"
              value={formData.ownerName || ""}
              onChange={onFieldChange}
              size="small"
              error={fieldError("ownerName")}
              helperText={fieldHelper("ownerName")}
              disabled={!fieldsEnabled}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Title"
              name="ownerTitle"
              value={formData.ownerTitle || ""}
              onChange={onFieldChange}
              size="small"
              error={fieldError("ownerTitle")}
              helperText={fieldHelper("ownerTitle")}
              disabled={!fieldsEnabled}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              required
              label="Address"
              name="ownerAddress"
              value={formData.ownerAddress || ""}
              onChange={onFieldChange}
              size="small"
              error={fieldError("ownerAddress")}
              helperText={fieldHelper("ownerAddress")}
              disabled={!fieldsEnabled}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              label="Contact Number"
              name="ownerContactNo"
              value={formData.ownerContactNo || ""}
              onChange={onFieldChange}
              size="small"
              error={fieldError("ownerContactNo")}
              helperText={fieldHelper("ownerContactNo")}
              disabled={!fieldsEnabled}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              label="E-mail ID"
              name="ownerEmail"
              type="email"
              value={formData.ownerEmail || ""}
              onChange={onFieldChange}
              size="small"
              error={fieldError("ownerEmail")}
              helperText={fieldHelper("ownerEmail")}
              disabled={!fieldsEnabled}
            />
          </Grid>
        </Grid>
      </SectionCard>

      <SectionCard title="Existing- Sub-Contractor / Job work vendor">
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              label="Does vendor do job work for other brands or same brand? (Y/N)"
              name="existingJobWorkVendor"
              select
              SelectProps={{ native: true }}
              InputLabelProps={{ shrink: true }}
              value={formData.existingJobWorkVendor || ""}
              onChange={onFieldChange}
              size="small"
              error={fieldError("existingJobWorkVendor")}
              helperText={fieldHelper("existingJobWorkVendor")}
              disabled={!fieldsEnabled}
            >
              <option value="">Select</option>
              <option value="Y">Yes</option>
              <option value="N">No</option>
            </TextField>
          </Grid>

          <Grid item xs={12}>
            <FormikTextField
              multiline
              rows={3}
              label="If Yes: Names of the Factory / Brand (Mention all past/present brands worked with)"
              name="existingJobWorkVendorYesRemarks"
              disabled={
                !fieldsEnabled || formData.existingJobWorkVendor !== "Y"
              }
              helperText={
                formData.existingJobWorkVendor !== "Y"
                  ? "Enabled only if Yes"
                  : undefined
              }
            />
          </Grid>
        </Grid>
      </SectionCard>

      <SectionCard title="Reference / Scouring staff Details">
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="EMP ID No."
              name="referenceEmpId"
              value={formData.referenceEmpId || ""}
              onChange={onFieldChange}
              size="small"
              error={fieldError("referenceEmpId")}
              helperText={fieldHelper("referenceEmpId")}
              disabled={!fieldsEnabled}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              label="Referred By - Name"
              name="referredByName"
              value={formData.referredByName || ""}
              onChange={onFieldChange}
              size="small"
              error={fieldError("referredByName")}
              helperText={fieldHelper("referredByName")}
              disabled={!fieldsEnabled}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Referred By - Title"
              name="referredByTitle"
              value={formData.referredByTitle || ""}
              onChange={onFieldChange}
              size="small"
              error={fieldError("referredByTitle")}
              helperText={fieldHelper("referredByTitle")}
              disabled={!fieldsEnabled}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              label="Mobile No."
              name="referredByMobileNo"
              value={formData.referredByMobileNo || ""}
              onChange={onFieldChange}
              size="small"
              error={fieldError("referredByMobileNo")}
              helperText={fieldHelper("referredByMobileNo")}
              disabled={!fieldsEnabled}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              required
              label="Address"
              name="referredByAddress"
              value={formData.referredByAddress || ""}
              onChange={onFieldChange}
              size="small"
              error={fieldError("referredByAddress")}
              helperText={fieldHelper("referredByAddress")}
              disabled={!fieldsEnabled}
            />
          </Grid>

          <Grid item xs={12}>
            <FormControl
              component="fieldset"
              error={fieldError("referredFor")}
              required
            >
              <FormLabel component="legend">
                Referred for (select all that apply)
              </FormLabel>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 0.5 }}>
                {categoryOptions.map((opt) => (
                  <FormControlLabel
                    key={opt.value}
                    control={
                      <Checkbox
                        checked={referredForValue.includes(opt.value)}
                        onChange={() => handleReferredForChange(opt.value)}
                        name={`referredFor_${opt.value}`}
                        disabled={!fieldsEnabled}
                      />
                    }
                    label={opt.label}
                  />
                ))}
              </Box>
              {fieldHelper("referredFor") && (
                <Typography
                  variant="caption"
                  color="error"
                  sx={{ mt: 0.5, display: "block" }}
                >
                  {fieldHelper("referredFor")}
                </Typography>
              )}
            </FormControl>
          </Grid>
          {isReferredForOtherSelected && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Other - Description"
                name="referredForOtherRemarks"
                value={formData.referredForOtherRemarks || ""}
                onChange={onFieldChange}
                size="small"
                placeholder="Please describe"
                error={fieldError("referredForOtherRemarks")}
                helperText={fieldHelper("referredForOtherRemarks")}
                disabled={!fieldsEnabled}
              />
            </Grid>
          )}

          <Grid item xs={12}>
            <FormikTextField
              multiline
              rows={3}
              label="Feedback about the vendor"
              name="referenceFeedbackAboutVendor"
              disabled={!fieldsEnabled}
            />
          </Grid>
        </Grid>
      </SectionCard>

      <SectionCard title="Bank Details">
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              required
              label="PAN #"
              name="panNo"
              value={formData.panNo || ""}
              onChange={onFieldChange}
              onBlur={onPanBlur}
              size="small"
              error={fieldError("panNo")}
              helperText={fieldHelper("panNo")}
              disabled={!fieldsEnabled}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Bank Name"
              name="bankName"
              value={formData.bankName || ""}
              onChange={onFieldChange}
              size="small"
              error={fieldError("bankName")}
              helperText={fieldHelper("bankName")}
              disabled={!fieldsEnabled}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Account No."
              name="accountNo"
              value={formData.accountNo || ""}
              onChange={onFieldChange}
              size="small"
              error={fieldError("accountNo")}
              helperText={fieldHelper("accountNo")}
              disabled={!fieldsEnabled}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="IFSC Code"
              name="ifscCode"
              value={formData.ifscCode || ""}
              onChange={onFieldChange}
              size="small"
              error={fieldError("ifscCode")}
              helperText={fieldHelper("ifscCode")}
              disabled={!fieldsEnabled}
            />
          </Grid>
        </Grid>
      </SectionCard>
    </Box>
  );
};

export default Step1FactoryEvaluation;
